import type { ClassSession, SessionStatus, TeachingContent } from '../types/academic';
import { DEMO_CLASSES } from './demoData';

interface SessionTemplate {
  id: string;
  classIndex: number;
  dayOffset: number;
  hour: number;
  durationMinutes: number;
  status: SessionStatus;
  scheduledCoachId?: string;
  actualCoachId?: string;
  notes?: string;
  teaching?: TeachingContent;
}

const TEMPLATES: SessionTemplate[] = [
  {
    id: 'demo-session-001',
    classIndex: 0,
    dayOffset: 0,
    hour: 10,
    durationMinutes: 60,
    status: 'scheduled',
    teaching: {
      topics: ['Basic step', 'Cross body lead'],
      summary: 'Timing on 1-2-3, 5-6-7 and a clean slot change.',
      homework: 'Practice basic step 10 minutes daily.',
    },
  },
  {
    id: 'demo-session-002',
    classIndex: 1,
    dayOffset: 0,
    hour: 12,
    durationMinutes: 90,
    status: 'scheduled',
  },
  {
    id: 'demo-session-003',
    classIndex: 2,
    dayOffset: 0,
    hour: 14,
    durationMinutes: 120,
    status: 'scheduled',
    notes: 'Bring comfortable shoes',
  },
  {
    id: 'demo-session-004',
    classIndex: 0,
    dayOffset: -2,
    hour: 18,
    durationMinutes: 60,
    status: 'completed',
    teaching: {
      topics: ['Basic cross-body lead'],
      summary: 'Slot change with a clean frame.',
      homework: 'Practice the lead without looking at feet.',
    },
  },
  {
    id: 'demo-session-005',
    classIndex: 1,
    dayOffset: 2,
    hour: 18,
    durationMinutes: 90,
    status: 'scheduled',
    scheduledCoachId: 'demo-coach-002',
    actualCoachId: 'demo-coach-001',
  },
  {
    id: 'demo-session-006',
    classIndex: 0,
    dayOffset: -5,
    hour: 10,
    durationMinutes: 60,
    status: 'completed',
    teaching: {
      topics: ['Rhythm and weight change'],
      summary: 'Finding the 1 without rushing.',
    },
  },
  {
    id: 'demo-session-007',
    classIndex: 1,
    dayOffset: 5,
    hour: 12,
    durationMinutes: 90,
    status: 'scheduled',
  },
  {
    id: 'demo-session-008',
    classIndex: 0,
    dayOffset: -1,
    hour: 11,
    durationMinutes: 60,
    status: 'cancelled',
  },
  {
    id: 'demo-session-009',
    classIndex: 1,
    dayOffset: -7,
    hour: 18,
    durationMinutes: 90,
    status: 'completed',
    teaching: {
      topics: ['Body isolation'],
      summary: 'Keep the ribcage moving independently of the hips.',
    },
  },
  {
    id: 'demo-session-010',
    classIndex: 1,
    dayOffset: -14,
    hour: 18,
    durationMinutes: 90,
    status: 'completed',
    teaching: {
      topics: ['Basic turn pattern'],
    },
  },
];

function unixAt(dayOffset: number, hour: number): number {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hour, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

function fromTemplate(template: SessionTemplate, now: number): ClassSession {
  const cls = DEMO_CLASSES[template.classIndex];
  const scheduledStart = unixAt(template.dayOffset, template.hour);
  return {
    _id: template.id,
    classId: cls._id,
    scheduledStart,
    scheduledEnd: scheduledStart + template.durationMinutes * 60,
    durationMinutes: template.durationMinutes,
    coachAssignment: {
      scheduledCoachId: template.scheduledCoachId ?? cls.coachId,
      actualCoachId: template.actualCoachId,
    },
    status: template.status,
    notes: template.notes,
    teaching: template.teaching,
    createdAt: now,
    updatedAt: now,
  };
}

export function buildDemoSessionsForToday(): ClassSession[] {
  const now = Math.floor(Date.now() / 1000);
  return TEMPLATES.map((template) => fromTemplate(template, now));
}

/** Re-anchor template sessions to the current calendar while keeping user edits. */
export function normalizeSessionsForToday(sessions: ClassSession[]): ClassSession[] {
  const templates = buildDemoSessionsForToday();
  const byId = Object.fromEntries(templates.map((s) => [s._id, s]));
  const seen = new Set<string>();

  const merged = sessions.map((session) => {
    const template = byId[session._id];
    if (!template) return session;
    seen.add(session._id);
    return {
      ...session,
      scheduledStart: template.scheduledStart,
      scheduledEnd: template.scheduledEnd,
      durationMinutes: session.durationMinutes ?? template.durationMinutes,
      status: session.status === 'cancelled' || session.status === 'completed' ? session.status : template.status,
      coachAssignment: session.coachAssignment.actualCoachId
        ? session.coachAssignment
        : template.coachAssignment,
      teaching: session.teaching ?? template.teaching,
    };
  });

  for (const template of templates) {
    if (!seen.has(template._id)) merged.push(template);
  }
  return merged;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export { clone as cloneDemoData };
